import React, { useState } from 'react';
import { API_ENDPOINTS } from '../config/api';

// Helper function to get API endpoint for individual processes
const getProcessEndpoint = (processId) => {
  return `${API_ENDPOINTS.ANALYSIS_PROCESSES}${processId}/`;
};

const AnalysisProcesses = ({ processes, onUpdate, channels, websites }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProcess, setEditingProcess] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    process_type: 'keyword_search',
    base_prompt: '',
    can_update_swot: false,
    can_process_pdfs: false,
    can_send_notifications: true,
    selected_sources: []
  });

  const processTypes = [
    { value: 'keyword_search', label: 'Keyword Search', description: 'Search for specific keywords in content' },
    { value: 'swot_analysis', label: 'SWOT Analysis', description: 'Analyze content for strengths, weaknesses, opportunities, threats' },
    { value: 'financial_insights', label: 'Financial Insights', description: 'Extract financial metrics and insights' },
    { value: 'sentiment_analysis', label: 'Sentiment Analysis', description: 'Analyze market sentiment and tone' },
    { value: 'custom', label: 'Custom Analysis', description: 'Custom analysis with your own prompt' }
  ];

  const handleAddProcess = () => {
    setFormData({
      name: '',
      description: '',
      process_type: 'keyword_search',
      base_prompt: '',
      can_update_swot: false,
      can_process_pdfs: false,
      can_send_notifications: true,
      selected_sources: []
    });
    setEditingProcess(null);
    setShowAddForm(true);
  };

  const handleEditProcess = (process) => {
    setFormData({
      name: process.name,
      description: process.description,
      process_type: process.process_type,
      base_prompt: process.base_prompt,
      can_update_swot: process.can_update_swot,
      can_process_pdfs: process.can_process_pdfs,
      can_send_notifications: process.can_send_notifications,
      selected_sources: process.selected_sources || []
    });
    setEditingProcess(process);
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const processData = {
      ...formData,
      is_active: true,
      is_template: false
    };

    try {
      if (editingProcess) {
        // Update existing process via API
        const response = await fetch(getProcessEndpoint(editingProcess.id), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(processData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to update process');
        }
        
        const updatedProcess = await response.json();
        const updatedProcesses = processes.map(p => 
          p.id === editingProcess.id ? updatedProcess : p
        );
        onUpdate(updatedProcesses);
      } else {
        // Add new process via API
        const response = await fetch(API_ENDPOINTS.ANALYSIS_PROCESSES, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(processData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to add process');
        }
        
        const newProcess = await response.json();
        onUpdate([...processes, newProcess]);
      }
      
      setShowAddForm(false);
      setEditingProcess(null);
      setFormData({
        name: '',
        description: '',
        process_type: 'keyword_search',
        base_prompt: '',
        can_update_swot: false,
        can_process_pdfs: false,
        can_send_notifications: true,
        selected_sources: []
      });
    } catch (error) {
      console.error('Error saving process:', error);
    }
  };

  const handleToggleActive = async (processId) => {
    try {
      const process = processes.find(p => p.id === processId);
      const response = await fetch(getProcessEndpoint(processId), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !process.is_active })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update process status');
      }
      
      const updatedProcess = await response.json();
      const updatedProcesses = processes.map(p => 
        p.id === processId ? updatedProcess : p
      );
      onUpdate(updatedProcesses);
    } catch (error) {
      console.error('Error updating process status:', error);
    }
  };

  const handleDeleteProcess = async (processId) => {
    if (window.confirm('Are you sure you want to delete this analysis process?')) {
      try {
        const response = await fetch(getProcessEndpoint(processId), {
          method: 'DELETE',
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete process');
        }
        
        const updatedProcesses = processes.filter(p => p.id !== processId);
        onUpdate(updatedProcesses);
      } catch (error) {
        console.error('Error deleting process:', error);
      }
    }
  };

  const getProcessTypeColor = (type) => {
    switch (type) {
      case 'keyword_search': return 'text-blue-600 bg-blue-100';
      case 'swot_analysis': return 'text-green-600 bg-green-100';
      case 'financial_insights': return 'text-purple-600 bg-purple-100';
      case 'sentiment_analysis': return 'text-orange-600 bg-orange-100';
      case 'custom': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDefaultPrompt = (type) => {
    switch (type) {
      case 'keyword_search':
        return 'Search for these keywords in the content: [keywords]';
      case 'swot_analysis':
        return 'Analyze this content for SWOT (Strengths, Weaknesses, Opportunities, Threats) impact on stocks.';
      case 'financial_insights':
        return 'Extract financial metrics, earnings data, and market-moving insights from this content.';
      case 'sentiment_analysis':
        return 'Analyze the market sentiment and tone of this content. Is it bullish, bearish, or neutral?';
      case 'custom':
        return 'Enter your custom analysis prompt here...';
      default:
        return '';
    }
  };

  const handleProcessTypeChange = (type) => {
    setFormData({
      ...formData,
      process_type: type,
      base_prompt: getDefaultPrompt(type)
    });
  };

  return (
    <div className="p-6 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Analysis Processes</h2>
        <button
          onClick={handleAddProcess}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Add Process</span>
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-6 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingProcess ? 'Edit Analysis Process' : 'Add New Analysis Process'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Process Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Tech News Monitor"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Process Type *
                </label>
                <select
                  required
                  value={formData.process_type}
                  onChange={(e) => handleProcessTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {processTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe what this process does..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base Prompt *
              </label>
              <textarea
                required
                value={formData.base_prompt}
                onChange={(e) => setFormData({ ...formData, base_prompt: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter the base prompt for this analysis process..."
              />
              <p className="mt-1 text-xs text-gray-500">
                This prompt will be used as the foundation for analyzing content. You can include placeholders like [keywords] or [content] that will be replaced automatically.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Sources to Monitor *
              </label>
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">YouTube Channels</h4>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {channels && channels.length > 0 ? (
                      channels.map(channel => (
                        <label key={channel.id} className="flex items-center space-x-2">
                                                  <input
                          type="checkbox"
                          checked={(formData.selected_sources || []).includes(channel.id)}
                          onChange={(e) => {
                            const currentSources = formData.selected_sources || [];
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                selected_sources: [...currentSources, channel.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                selected_sources: currentSources.filter(id => id !== channel.id)
                              });
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                          <span className="text-sm text-gray-900">{channel.name}</span>
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 col-span-2">No channels available</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Websites</h4>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {websites && websites.length > 0 ? (
                      websites.map(website => (
                        <label key={website.id} className="flex items-center space-x-2">
                                                  <input
                          type="checkbox"
                          checked={(formData.selected_sources || []).includes(website.id)}
                          onChange={(e) => {
                            const currentSources = formData.selected_sources || [];
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                selected_sources: [...currentSources, website.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                selected_sources: currentSources.filter(id => id !== website.id)
                              });
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                          <span className="text-sm text-gray-900">{website.name}</span>
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 col-span-2">No websites available</p>
                    )}
                  </div>
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Select which data sources this analysis process should monitor. The process will only analyze content from the selected sources.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="can_update_swot"
                  checked={formData.can_update_swot}
                  onChange={(e) => setFormData({ ...formData, can_update_swot: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="can_update_swot" className="ml-2 block text-sm text-gray-900">
                  Can Update SWOT
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="can_process_pdfs"
                  checked={formData.can_process_pdfs}
                  onChange={(e) => setFormData({ ...formData, can_process_pdfs: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="can_process_pdfs" className="ml-2 block text-sm text-gray-900">
                  Can Process PDFs
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="can_send_notifications"
                  checked={formData.can_send_notifications}
                  onChange={(e) => setFormData({ ...formData, can_send_notifications: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="can_send_notifications" className="ml-2 block text-sm text-gray-900">
                  Send Notifications
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingProcess(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingProcess ? 'Update Process' : 'Add Process'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Processes List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Process
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Capabilities
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {processes.map((process) => (
                <tr key={process.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{process.name}</div>
                      <div className="text-sm text-gray-500">{process.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProcessTypeColor(process.process_type)}`}>
                      {processTypes.find(t => t.value === process.process_type)?.label || process.process_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      process.is_active ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'
                    }`}>
                      {process.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {process.can_update_swot && (
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                          SWOT
                        </span>
                      )}
                      {process.can_process_pdfs && (
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          PDF
                        </span>
                      )}
                      {process.can_send_notifications && (
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                          Notify
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleToggleActive(process.id)}
                        className={`px-2 py-1 rounded text-xs ${
                          process.is_active 
                            ? 'bg-red-100 text-red-800 hover:bg-red-200' 
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {process.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleEditProcess(process)}
                        className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs hover:bg-blue-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteProcess(process.id)}
                        className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {processes.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No analysis processes</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating your first analysis process.</p>
            <div className="mt-6">
              <button
                onClick={handleAddProcess}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Process
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisProcesses;
